import type { Ref } from 'react';
import { Box, Card, Typography } from '@mui/material';
import type { KeyValueRow } from './KeyValueCard';

/** A titled group of label/value rows inside a {@link SectionedKeyValueCard}. */
export interface KeyValueSection {
  /** Uppercase section band label. Omit for an untitled leading group. */
  label?: string;
  rows: KeyValueRow[];
}

export interface SectionedKeyValueCardProps {
  /** Card header title. */
  title: string;
  /** The titled sections, rendered top to bottom. */
  sections: KeyValueSection[];
  ref?: Ref<HTMLDivElement>;
}

/**
 * Multi-section definition card (the Polaris kit `LineGroupCard`): one outlined
 * card with a header, then a vertical stack of titled sections — each an
 * uppercase band header over label/value rows with hairline separators. Rows
 * reuse {@link KeyValueRow} (supports `mono`, `hint`, and `wrap`). Used by the
 * request detail summary and reused by the invoice detail (slice 15).
 */
export function SectionedKeyValueCard({ title, sections, ref }: SectionedKeyValueCardProps) {
  return (
    <Card ref={ref} variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box sx={{ p: '14px 18px', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography component="div" sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>
          {title}
        </Typography>
      </Box>
      {sections.map((section, si) => (
        <Box key={si}>
          {section.label && (
            <Box
              sx={{
                p: '8px 18px',
                bgcolor: 'tableHeaderBg',
                borderBottom: '1px solid',
                borderColor: 'divider',
                borderTop: si > 0 ? '1px solid' : 'none',
                borderTopColor: 'divider',
              }}
            >
              <Typography
                component="div"
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                }}
              >
                {section.label}
              </Typography>
            </Box>
          )}
          {section.rows.map((row, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 2,
                p: '11px 18px',
                borderBottom: '1px solid',
                borderColor: 'borderSubtle',
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  letterSpacing: '0.4px',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  flexShrink: 0,
                }}
              >
                {row.label}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 1,
                  minWidth: 0,
                  justifyContent: 'flex-end',
                  textAlign: 'right',
                }}
              >
                <Typography
                  component="span"
                  title={typeof row.value === 'string' ? row.value : undefined}
                  sx={{
                    fontSize: row.mono ? 13 : 14,
                    fontWeight: 500,
                    color: 'text.primary',
                    ...(row.wrap
                      ? { whiteSpace: 'normal', overflowWrap: 'break-word' }
                      : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
                    ...(row.mono
                      ? { fontFamily: 'var(--font-mono, ui-monospace, monospace)' }
                      : {}),
                  }}
                >
                  {row.value}
                </Typography>
                {row.hint && (
                  <Typography
                    component="span"
                    sx={{ fontSize: 10, color: 'text.secondary', flexShrink: 0 }}
                  >
                    {row.hint}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      ))}
    </Card>
  );
}
